import { useEffect, useRef, type ReactNode } from 'react';

/**
 * A window over the page: one record, opened, with everything about it in one
 * place and nothing about anything else.
 *
 * The alternative — and what this replaced — is a screen where editing, the
 * new password and the add form all unfold as panels above a table, so the
 * page rearranges itself under the reader and three teachers' worth of
 * controls sit in one strip of buttons. A record you can open is calmer and
 * says plainly which account you are working on.
 *
 * It slides in from the side rather than landing in the middle, so the roster
 * stays visible behind it: this is a drawer being pulled out of a cabinet, not
 * a different screen.
 */
export function Sheet({
  open,
  title,
  label,
  eyebrow,
  aside,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: ReactNode;
  /** What a screen reader announces, where the title is more than words. */
  label?: string;
  eyebrow?: string;
  /** Sits under the title: the identifying line for this record. */
  aside?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  const cameFrom = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    cameFrom.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // The panel itself takes focus, not the first field: a record is read
    // before it is edited, and a cursor blinking in a name box invites typing
    // over somebody's name by accident.
    panel.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);

    // The page behind must not scroll away under the window.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
      cameFrom.current?.focus();
      cameFrom.current = null;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="sheet"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-label={label ?? (typeof title === 'string' ? title : undefined)}
        tabIndex={-1}
        ref={panel}
      >
        <header className="sheet__head">
          <div>
            {eyebrow && <p className="eyebrow">{eyebrow}</p>}
            <h2 className="sheet__title">{title}</h2>
            {aside && <div className="sheet__aside">{aside}</div>}
          </div>
          <button type="button" className="sheet__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="sheet__body">{children}</div>

        {footer && <footer className="sheet__foot">{footer}</footer>}
      </div>
    </div>
  );
}
